import {useState, useEffect, useRef, useCallback} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {trpc, createMutationErrorHandler} from '@common-web/lib/trpc';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {Alert, AlertDescription, AlertTitle} from '@common-web/components/ui/alert';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {Checkbox} from '@common-web/components/ui/checkbox';
import {ScrollArea} from '@common-web/components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@common-web/components/ui/alert-dialog';
import {toast} from '@common-web/hooks/use-toast';
import {useAuthStore} from '@common-web/stores/authStore';
import {useClientActionAllowed} from '@common-web/hooks/use-permissions';
import {CODE_PERMISSIONS} from '@common/permissions';
import {PageHeader, PageHeaderAction} from '@common-web/components/page-header';
import {
  ArrowLeft,
  Shield,
  Building2,
  UsersIcon,
  Calendar,
  AlertCircle,
  User,
  ChevronRight,
  Edit2,
  Save,
  X,
  Loader2,
  Search,
  Monitor,
  Globe,
  Send,
  Archive,
  ArchiveRestore,
} from 'lucide-react';
import {getUserTypeBadge} from './components/userTypeBadges';
import {format} from 'date-fns';
import {
  FeatureFlagDisplay,
  FeatureFlagEditableCard,
  FeatureFlagsState,
  getDefaultFeatureFlags,
} from './components/featureFlagConfig';
import {ChangeUserRole} from './components/ChangeUserRole';

export function InternalUserDetail() {
  const isInternalUser = useAuthStore((state) => state.isInternalUser());
  const currentUserId = useAuthStore((state) => state.user?.id);
  // Mirror the server-side gates in user-management-router: these only
  // constrain client-profile viewers — internal users always pass.
  const canEditUser = useClientActionAllowed(CODE_PERMISSIONS.userManagement.editUserBasic.key);
  const canArchiveUser = useClientActionAllowed(CODE_PERMISSIONS.userManagement.archiveUser.key);
  const canResendInvite = useClientActionAllowed(CODE_PERMISSIONS.userManagement.inviteUser.key);
  const {id} = useParams<{id: string}>();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [groupSearch, setGroupSearch] = useState('');
  const [clientId, setClientId] = useState<string | null>(null);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [isUnarchiveDialogOpen, setIsUnarchiveDialogOpen] = useState(false);
  const [isResendInviteDialogOpen, setIsResendInviteDialogOpen] = useState(false);

  const {
    data: user,
    isLoading,
    isFetching,
    error,
    refetch,
  } = trpc.internal.userManagement.getUser.useQuery({id: id!}, {enabled: !!id});

  const {
    data: groupsData,
    isLoading: groupsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = trpc.internal.userManagement.getGroups.useInfiniteQuery(
    {
      clientId: clientId || undefined,
      search: groupSearch,
    },
    {
      enabled: !!clientId && isEditing && user?.type === 'GROUP',
      getNextPageParam: (lastPage) => lastPage.nextCursor,
    },
  );

  const groups = groupsData?.pages.flatMap((page) => page.groups) ?? [];

  const scrollObserverRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (scrollObserverRef.current) scrollObserverRef.current.disconnect();
      if (!node) return;
      scrollObserverRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      });
      scrollObserverRef.current.observe(node);
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  const {data: groupPresets} = trpc.internal.userManagement.getGroupPresets.useQuery(undefined, {
    enabled: isInternalUser && isEditing,
  });

  const getPresetGroups = trpc.internal.userManagement.getPresetGroups.useMutation();
  const [loadingPreset, setLoadingPreset] = useState(false);
  const handlePresetSelect = async (presetKey: 'pronto') => {
    setLoadingPreset(true);
    try {
      const result = await getPresetGroups.mutateAsync({preset: presetKey});
      if (result) {
        setEditForm({
          ...editForm,
          selectedGroups: result.map((g) => ({
            vbaGroupId: g.vbaGroupId,
            groupName: g.groupName,
            status: g.status,
          })),
        });
        toast({
          title: 'Preset Applied',
          description: `Selected ${result.length} groups`,
        });
      }
    } catch {
      toast({
        title: 'Error loading preset',
        description: 'Failed to load preset groups',
        variant: 'destructive',
      });
    } finally {
      setLoadingPreset(false);
    }
  };

  const updateUser = trpc.internal.userManagement.updateUser.useMutation({
    onSuccess: () => {
      toast({
        title: 'User updated',
        description: 'User details have been successfully updated.',
      });
      setIsEditing(false);
      refetch();
    },
    onError: createMutationErrorHandler('Error updating user'),
  });

  const archiveUser = trpc.internal.userManagement.archiveUser.useMutation({
    onSuccess: () => {
      toast({
        title: 'User archived',
        description: 'The user can no longer sign in. You can restore them from the Archived tab.',
      });
      setIsArchiveDialogOpen(false);
      refetch();
    },
    onError: createMutationErrorHandler('Error archiving user'),
  });

  const unarchiveUser = trpc.internal.userManagement.unarchiveUser.useMutation({
    onSuccess: () => {
      toast({
        title: 'User restored',
        description: 'The user has been unarchived and can sign in again.',
      });
      setIsUnarchiveDialogOpen(false);
      refetch();
    },
    onError: createMutationErrorHandler('Error unarchiving user'),
  });

  const resendUserInvite = trpc.internal.userManagement.resendUserInvite.useMutation({
    onSuccess: (data) => {
      toast({
        title: 'Invite sent',
        description: `A new portal invite email has been sent to ${data.email}.`,
      });
      setIsResendInviteDialogOpen(false);
    },
    onError: createMutationErrorHandler('Error sending invite'),
  });

  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    featureFlags: getDefaultFeatureFlags(),
    selectedGroups: [] as {vbaGroupId: string; groupName: string; status: string}[],
  });

  const resetEditForm = () => {
    if (user) {
      if (user.type === 'GROUP' && user.groupProfiles.length > 0) {
        setClientId(user.groupProfiles[0].group.client.id);
      }

      const defaults = getDefaultFeatureFlags();
      const userFlags = (user.featureFlags ?? {}) as FeatureFlagsState;
      setEditForm({
        name: user.name,
        email: user.email,
        featureFlags: Object.fromEntries(
          Object.keys(defaults).map((key) => [key, !!userFlags[key]]),
        ),
        selectedGroups: user.groupProfiles
          .filter((ga) => !!ga.group.vbaGroupId)
          .map((ga) => ({
            vbaGroupId: ga.group.vbaGroupId!,
            groupName: ga.group.name,
            status: ga.group.groupStatus,
          })),
      });
    } else {
      setEditForm({
        name: '',
        email: '',
        featureFlags: getDefaultFeatureFlags(),
        selectedGroups: [],
      });
    }
  };

  useEffect(() => {
    if (user) {
      resetEditForm();
    }
  }, [user]);

  const handleSave = () => {
    if (editForm.email !== user?.email && editForm.email.trim() === '') {
      toast({
        title: 'Invalid email',
        description: 'Email address cannot be empty.',
        variant: 'destructive',
      });
      return;
    }

    updateUser.mutate({
      id: id!,
      name: editForm.name,
      email: editForm.email !== user?.email ? editForm.email : undefined,
      featureFlags: editForm.featureFlags,
      groupAccess:
        user?.type === 'GROUP'
          ? editForm.selectedGroups.map((g) => ({vbaGroupId: g.vbaGroupId}))
          : undefined,
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    resetEditForm();
  };

  const handleGroupToggle = (group: {vbaGroupId: string; groupName: string; status: string}) => {
    const isSelected = editForm.selectedGroups.some((g) => g.vbaGroupId === group.vbaGroupId);
    if (isSelected) {
      setEditForm({
        ...editForm,
        selectedGroups: editForm.selectedGroups.filter((g) => g.vbaGroupId !== group.vbaGroupId),
      });
    } else {
      setEditForm({
        ...editForm,
        selectedGroups: [...editForm.selectedGroups, group],
      });
    }
  };

  if (isLoading) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
        <div className='flex flex-col gap-0'>
          <PageHeader title='User' icon={<User className='h-6 w-6' />} isLoading />
          <div className='px-8 py-6 max-w-7xl mx-auto w-full'>
            <Card>
              <CardContent className='p-6 space-y-6'>
                <div className='flex items-center gap-4'>
                  <Skeleton className='h-16 w-16 rounded-full' />
                  <div className='space-y-2'>
                    <Skeleton className='h-6 w-48' />
                    <Skeleton className='h-4 w-32' />
                  </div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <Skeleton className='h-24 w-full' />
                  <Skeleton className='h-24 w-full' />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className='min-h-screen bg-gray-50 flex items-center justify-center p-8'>
        <Card className='w-full max-w-md'>
          <CardContent className='p-6 text-center space-y-4'>
            <AlertCircle className='h-12 w-12 text-red-500 mx-auto' />
            <h2 className='text-xl font-semibold text-brand-navy'>User Not Found</h2>
            <p className='text-brand-gray'>
              {error?.message ||
                "The user you're looking for doesn't exist or you don't have permission to view them."}
            </p>
            <Button onClick={() => navigate('/users')} variant='outline'>
              Back to Users
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const canManageUser = isInternalUser || user.type !== 'INTERNAL';
  // Role changes go through an internal-only procedure (setUserRole), so the
  // control is hidden from client-portal admins even when they can otherwise
  // manage the user.
  const canChangeRole = isInternalUser && canManageUser;
  const isSelf = currentUserId === user.id;

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        <PageHeader
          title={user.name}
          description={user.email}
          icon={<User className='h-6 w-6' />}
          isFetching={isFetching}
        >
          {isEditing ? (
            <>
              <PageHeaderAction
                tone='secondary'
                onClick={handleCancel}
                disabled={updateUser.isPending}
              >
                <X className='h-4 w-4 mr-2' />
                Cancel
              </PageHeaderAction>
              <PageHeaderAction onClick={handleSave} disabled={updateUser.isPending}>
                {updateUser.isPending ? (
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                ) : (
                  <Save className='h-4 w-4 mr-2' />
                )}
                Save Changes
              </PageHeaderAction>
            </>
          ) : (
            canManageUser &&
            (user.archivedAt ? (
              canArchiveUser && (
                <PageHeaderAction
                  onClick={() => setIsUnarchiveDialogOpen(true)}
                  className='bg-emerald-600 hover:bg-emerald-700 text-white'
                >
                  <ArchiveRestore className='h-4 w-4 mr-2' />
                  Unarchive
                </PageHeaderAction>
              )
            ) : (
              <>
                {!user.lastLogin && canResendInvite && (
                  <PageHeaderAction
                    tone='secondary'
                    onClick={() => setIsResendInviteDialogOpen(true)}
                  >
                    <Send className='h-4 w-4 mr-2' />
                    Resend Invite
                  </PageHeaderAction>
                )}
                {canEditUser && (
                  <PageHeaderAction tone='secondary' onClick={() => setIsEditing(true)}>
                    <Edit2 className='h-4 w-4 mr-2' />
                    Edit User
                  </PageHeaderAction>
                )}
                {canArchiveUser && (
                  <PageHeaderAction
                    onClick={() => setIsArchiveDialogOpen(true)}
                    disabled={isSelf}
                    title={isSelf ? 'You cannot archive yourself' : undefined}
                    className='bg-red-600 hover:bg-red-700 text-white disabled:opacity-50'
                  >
                    <Archive className='h-4 w-4 mr-2' />
                    Archive
                  </PageHeaderAction>
                )}
              </>
            ))
          )}
        </PageHeader>
        <div className='px-8 py-6 max-w-7xl mx-auto w-full'>
          <div className='flex flex-wrap items-center justify-between gap-4 mb-6'>
            <Button
              variant='ghost'
              className='-ml-2 text-brand-gray hover:text-brand-navy'
              onClick={() => navigate('/users')}
            >
              <ArrowLeft className='h-4 w-4 mr-2' />
              Back to Users
            </Button>
            <div className='flex items-center gap-3'>
              {user.archivedAt && (
                <Badge className='bg-red-100 text-red-700 border border-red-200 font-medium'>
                  <Archive className='h-3 w-3 mr-1' />
                  Archived
                </Badge>
              )}
              {getUserTypeBadge(user.type)}
              <span className='text-sm text-brand-gray'>
                ID: <span className='font-mono opacity-70'>{user.id}</span>
              </span>
            </div>
          </div>
          {user.archivedAt && (
            <Alert className='mb-6 border-red-200 bg-red-50'>
              <Archive className='h-4 w-4 text-red-600' />
              <AlertTitle className='text-red-900'>This user is archived</AlertTitle>
              <AlertDescription className='text-red-800'>
                Archived {format(new Date(user.archivedAt), 'MMMM d, yyyy')}
                {user.archivedBy ? ` by ${user.archivedBy.name}` : ''}. They cannot sign in until
                restored.
              </AlertDescription>
            </Alert>
          )}
          <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
            <div className='lg:col-span-2 space-y-6'>
              <Card className='border-gray-200 shadow-sm'>
                <CardHeader className='bg-gray-50 border-b'>
                  <CardTitle className='text-lg font-semibold text-brand-navy'>
                    Account Details
                  </CardTitle>
                </CardHeader>
                <CardContent className='p-6 grid grid-cols-1 md:grid-cols-2 gap-6'>
                  {isEditing && (
                    <>
                      <div>
                        <h3 className='text-sm font-medium text-brand-gray mb-1'>Name</h3>
                        <Input
                          value={editForm.name}
                          onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                        />
                      </div>
                      <div>
                        <h3 className='text-sm font-medium text-brand-gray mb-1'>Email</h3>
                        <Input
                          type='email'
                          value={editForm.email}
                          onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                        />
                      </div>
                    </>
                  )}
                  <div>
                    <h3 className='text-sm font-medium text-brand-gray mb-1'>Account Type</h3>
                    <div className='font-medium text-brand-navy'>{user.type}</div>
                  </div>
                  <div>
                    <h3 className='text-sm font-medium text-brand-gray mb-1'>Created At</h3>
                    <div className='flex items-center gap-2 text-brand-navy'>
                      <Calendar className='h-4 w-4 text-brand-gray' />
                      {format(new Date(user.createdAt), 'MMMM d, yyyy')}
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className='border-gray-200 shadow-sm'>
                <CardHeader className='bg-gray-50 border-b'>
                  <CardTitle className='text-lg font-semibold text-brand-navy'>
                    Access & Permissions
                  </CardTitle>
                </CardHeader>
                <CardContent className='p-6 space-y-6'>
                  {user.clientProfile && (
                    <div>
                      <h3 className='text-sm font-medium text-brand-gray mb-3'>Client Access</h3>
                      <div className='space-y-2'>
                        <div
                          key={user.clientProfile.clientId}
                          className='flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100'
                        >
                          <div className='flex items-center gap-3'>
                            <div className='p-2 bg-white rounded-md border border-gray-200'>
                              <Building2 className='h-5 w-5 text-brand-blue' />
                            </div>
                            <div>
                              <div className='font-medium text-brand-navy'>
                                {user.clientProfile.client.name}
                              </div>
                              <div className='text-xs text-brand-gray'>
                                Role: {user.clientProfile.role.replace('client:', '')}
                              </div>
                            </div>
                          </div>
                          {canChangeRole && !user.archivedAt && (
                            <ChangeUserRole
                              userId={user.id}
                              userName={user.name}
                              scope='CLIENT'
                              clientId={user.clientProfile.clientId}
                              currentRole={user.clientProfile.role}
                              disabled={isSelf}
                              disabledReason='You cannot change your own role'
                              onRoleChanged={() => refetch()}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {user.type === 'GROUP' && (
                    <div>
                      <div className='flex items-center justify-between mb-3'>
                        <h3 className='text-sm font-medium text-brand-gray'>Group Access</h3>
                        {!isEditing &&
                          canChangeRole &&
                          !user.archivedAt &&
                          user.groupProfiles.length > 0 && (
                            <ChangeUserRole
                              userId={user.id}
                              userName={user.name}
                              scope='GROUP'
                              clientId={user.groupProfiles[0].group.client.id}
                              currentRole={user.groupProfiles[0].role}
                              disabled={isSelf}
                              disabledReason='You cannot change your own role'
                              onRoleChanged={() => refetch()}
                            />
                          )}
                        {isEditing && editForm.selectedGroups.length > 0 && (
                          <div className={'flex items-center gap-3'}>
                            <Badge className='bg-brand-blue/10 text-brand-blue border-0'>
                              {editForm.selectedGroups.length} selected
                            </Badge>
                            <Button
                              variant='ghost'
                              size='sm'
                              className='text-brand-red hover:text-red-600'
                              onClick={() => setEditForm({...editForm, selectedGroups: []})}
                            >
                              Clear All
                            </Button>
                          </div>
                        )}
                      </div>
                      {isEditing ? (
                        <div className='space-y-3'>
                          {isInternalUser &&
                            groupPresets &&
                            groupPresets
                              .filter((p) => p.clientId === clientId)
                              .map((preset) => (
                                <div key={preset.key} className='flex gap-2'>
                                  <Button
                                    size='sm'
                                    variant='outline'
                                    onClick={() => handlePresetSelect(preset.key)}
                                    disabled={loadingPreset}
                                    className='border-brand-blue text-brand-blue hover:bg-brand-blue/10'
                                  >
                                    {loadingPreset ? (
                                      <>
                                        <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                                        Loading...
                                      </>
                                    ) : (
                                      `Select ${preset.name} Groups (${preset.groupCount})`
                                    )}
                                  </Button>
                                </div>
                              ))}
                          <div className='relative'>
                            <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray' />
                            <Input
                              placeholder='Search groups...'
                              value={groupSearch}
                              onChange={(e) => setGroupSearch(e.target.value)}
                              className='pl-10'
                            />
                          </div>
                          <ScrollArea className='h-[300px] border rounded-lg p-3'>
                            {groupsLoading ? (
                              <div className='space-y-2'>
                                <Skeleton className='h-10 w-full' />
                                <Skeleton className='h-10 w-full' />
                                <Skeleton className='h-10 w-full' />
                              </div>
                            ) : groups.length > 0 ? (
                              <div className='space-y-2'>
                                {groups.map((group) => {
                                  const isSelected = editForm.selectedGroups.some(
                                    (g) => g.vbaGroupId === group.vbaGroupId,
                                  );
                                  return (
                                    <div
                                      key={group.vbaGroupId}
                                      className={`flex items-center space-x-3 p-2 rounded cursor-pointer hover:bg-gray-50 ${
                                        isSelected ? 'bg-brand-blue/5' : ''
                                      }`}
                                      onClick={() =>
                                        handleGroupToggle({
                                          vbaGroupId: group.vbaGroupId,
                                          groupName: group.groupName,
                                          status: group.status,
                                        })
                                      }
                                    >
                                      <Checkbox checked={isSelected} />
                                      <div className='flex-1'>
                                        <div className='font-medium text-brand-navy'>
                                          {group.groupName}
                                        </div>
                                        <div className='text-xs text-brand-gray'>
                                          Group ID: {group.vbaGroupId}
                                        </div>
                                      </div>
                                      <Badge
                                        className={
                                          group.status === 'ACTIVE'
                                            ? 'bg-emerald text-white border-0'
                                            : 'bg-brand-gray text-white border-0'
                                        }
                                      >
                                        {group.status}
                                      </Badge>
                                    </div>
                                  );
                                })}
                                {hasNextPage && (
                                  <div ref={loadMoreRef} className='flex justify-center py-2'>
                                    {isFetchingNextPage && (
                                      <Loader2 className='h-4 w-4 animate-spin text-brand-gray' />
                                    )}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className='text-center py-4 text-brand-gray'>
                                No groups found
                              </div>
                            )}
                          </ScrollArea>
                        </div>
                      ) : (
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                          {user.groupProfiles.map((access) => (
                            <div
                              key={access.groupId}
                              className='flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100 cursor-pointer hover:bg-gray-100 hover:border-gray-200 transition-all group'
                              onClick={() => navigate(`/groups/${access.group.originalAgentId}`)}
                            >
                              <div className='flex items-center gap-3'>
                                <div className='p-2 bg-white rounded-md border border-gray-200 group-hover:border-brand-blue/30 transition-colors'>
                                  <UsersIcon className='h-4 w-4 text-brand-gray group-hover:text-brand-blue transition-colors' />
                                </div>
                                <div className='flex flex-col'>
                                  <span className='font-medium text-brand-navy text-sm group-hover:text-brand-blue transition-colors'>
                                    {access.group.name}
                                  </span>
                                  <span className='text-xs text-brand-gray'>
                                    Client: {access.group.client.name}
                                  </span>
                                  <span className='text-xs text-brand-gray'>
                                    Role: {access.role.replace('group:', '')}
                                  </span>
                                </div>
                              </div>
                              <ChevronRight className='h-4 w-4 text-gray-300 group-hover:text-brand-blue transition-colors' />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {user.type === 'INTERNAL' && (
                    <div>
                      <div className='flex items-center justify-between mb-3'>
                        <h3 className='text-sm font-medium text-brand-gray'>Internal Access</h3>
                        {canChangeRole && !user.archivedAt && user.cbsInternalProfile && (
                          <ChangeUserRole
                            userId={user.id}
                            userName={user.name}
                            scope='INTERNAL'
                            currentRole={user.cbsInternalProfile.internalRole}
                            disabled={isSelf}
                            disabledReason='You cannot change your own role'
                            onRoleChanged={() => refetch()}
                          />
                        )}
                      </div>
                      <Alert className='bg-brand-navy/5 border-brand-navy/10'>
                        <Shield className='h-4 w-4 text-brand-navy' />
                        <AlertTitle className='flex items-center gap-2'>
                          Internal Administration
                          {user.role && (
                            <Badge variant='outline' className='ml-2 capitalize bg-white'>
                              {user.role.replace('internal:', '')}
                            </Badge>
                          )}
                        </AlertTitle>
                        <AlertDescription>
                          This user has internal access to the Infinity platform.
                        </AlertDescription>
                      </Alert>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            <div className='space-y-6'>
              <Card className='border-gray-200 shadow-sm'>
                <CardHeader className='bg-gray-50 border-b flex items-center justify-between'>
                  <CardTitle className='text-lg font-semibold text-brand-navy'>
                    Feature Access
                  </CardTitle>
                  {isEditing && (
                    <Badge className='bg-amber/10 text-amber border-amber/20'>Editing</Badge>
                  )}
                </CardHeader>
                <CardContent className='p-6'>
                  {isEditing ? (
                    <FeatureFlagEditableCard
                      idPrefix='detail'
                      flags={editForm.featureFlags}
                      onChange={(featureFlags) => setEditForm({...editForm, featureFlags})}
                    />
                  ) : (
                    <FeatureFlagDisplay flags={user.featureFlags as FeatureFlagsState} />
                  )}
                </CardContent>
              </Card>
              {isInternalUser && (
                <Card className='border-gray-200 shadow-sm'>
                  <CardHeader className='bg-gray-50 border-b'>
                    <CardTitle className='text-lg font-semibold text-brand-navy flex items-center gap-2'>
                      <Monitor className='h-5 w-5' />
                      Sessions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className='p-6'>
                    {user.sessions && user.sessions.length > 0 ? (
                      <div className='space-y-3'>
                        {user.sessions.map((session) => {
                          const isExpired = new Date(session.expiresAt) < new Date();
                          return (
                            <div
                              key={session.id}
                              className={`p-3 rounded-lg border ${isExpired ? 'bg-gray-50 border-gray-200' : 'bg-emerald/5 border-emerald/20'}`}
                            >
                              <div className='flex items-center justify-between mb-2'>
                                <Badge
                                  className={
                                    isExpired
                                      ? 'bg-gray-100 text-gray-600 border-0'
                                      : 'bg-emerald/10 text-emerald border-0'
                                  }
                                >
                                  {isExpired ? 'Expired' : 'Active'}
                                </Badge>
                                <span className='text-xs text-brand-gray'>
                                  {format(new Date(session.createdAt), 'MMM d, yyyy h:mm a')}
                                </span>
                              </div>
                              {session.ipAddress && (
                                <div className='flex items-center gap-2 text-sm text-brand-gray mb-1'>
                                  <Globe className='h-3 w-3' />
                                  <span className='font-mono text-xs'>{session.ipAddress}</span>
                                </div>
                              )}
                              {session.userAgent && (
                                <div
                                  className='text-xs text-brand-gray truncate'
                                  title={session.userAgent}
                                >
                                  {session.userAgent.length > 60
                                    ? `${session.userAgent.substring(0, 60)}...`
                                    : session.userAgent}
                                </div>
                              )}
                              <div className='text-xs text-brand-gray mt-1'>
                                Expires: {format(new Date(session.expiresAt), 'MMM d, yyyy h:mm a')}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className='text-center py-4 text-brand-gray'>No sessions found</div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={isResendInviteDialogOpen} onOpenChange={setIsResendInviteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resend Invite to {user.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              We will email <span className='font-medium text-brand-navy'>{user.email}</span> a
              fresh portal invite with a one-time activation link. Any existing password they may
              have set will continue to work until they use the new link.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resendUserInvite.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                resendUserInvite.mutate({id: user.id});
              }}
              disabled={resendUserInvite.isPending}
              className='bg-brand-blue hover:bg-brand-blue-dark text-white'
            >
              {resendUserInvite.isPending ? (
                <>
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                  Sending...
                </>
              ) : (
                <>
                  <Send className='h-4 w-4 mr-2' />
                  Send Invite
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isArchiveDialogOpen} onOpenChange={setIsArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to archive this user? They will be moved to the Archived tab
              where you can restore them later. Their access permissions, sessions, and audit
              history are preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiveUser.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                archiveUser.mutate({id: user.id});
              }}
              disabled={archiveUser.isPending}
              className='bg-red-600 hover:bg-red-700 text-white'
            >
              {archiveUser.isPending ? (
                <>
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                  Archiving...
                </>
              ) : (
                <>
                  <Archive className='h-4 w-4 mr-2' />
                  Archive User
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isUnarchiveDialogOpen} onOpenChange={setIsUnarchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unarchive {user.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Restoring will let this user sign in again with their existing credentials and
              previously assigned access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unarchiveUser.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                unarchiveUser.mutate({id: user.id});
              }}
              disabled={unarchiveUser.isPending}
              className='bg-emerald-600 hover:bg-emerald-700 text-white'
            >
              {unarchiveUser.isPending ? (
                <>
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                  Unarchiving...
                </>
              ) : (
                <>
                  <ArchiveRestore className='h-4 w-4 mr-2' />
                  Unarchive User
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
